import { defineStore } from 'pinia'
import { makeUUID, submit1, submit2 } from '../api'
import { BAI, HMPS, K_SSES, PSS, UCLA, 스키마검사 } from '../config'
import { cal고독, cal물렁, cal버럭, cal엄격, cal콩콩 } from './grade'

type State = {
  currentStage: number
  uuid: string

  // Stage1
  이전사용유무: boolean | undefined

  // Stage2
  birthYear: string
  birthMonth: string
  birthDate: string

  // Stage3
  agree: boolean | null
  email: string
  phone: string
  name: string

  // Stage4
  결혼유무: string
  직업군: string
  성별: string

  // Stage5
  PSSInputs: number[]
  스키마Inputs: number[]

  // Stage6
  HMPSInputs: number[]
  BAIInputs: number[]
  K_SSESInputs: number[]
  UCLAInputs: number[]
}

const STORAGE_KEY = 'asdlkfksladf'

export const useStore = defineStore('main', {
  state: (): State => {
    if (window.localStorage && window.localStorage.getItem(STORAGE_KEY)) {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY))
      return saved
    }

    return {
      currentStage: 1,
      uuid: '',

      // Stage1
      이전사용유무: undefined,

      // Stage2
      birthYear: '1990',
      birthMonth: '1',
      birthDate: '1',

      // Stage3
      agree: null,
      email: '',
      phone: '',
      name: '',

      // Stage4
      결혼유무: '',
      직업군: '',
      성별: '',

      // Stage5
      PSSInputs: [],
      스키마Inputs: [],

      // Stage6
      HMPSInputs: [],
      BAIInputs: [],
      K_SSESInputs: [],
      UCLAInputs: [],
    }
  },
  getters: {
    schemaResultCode: (state): string => {
      const inputs = state.스키마Inputs.map(String)
      const score = [
        `${cal엄격(inputs).toFixed(1)}`,
        `${cal물렁(inputs).toFixed(1)}`,
        `${cal고독(inputs).toFixed(1)}`,
        `${cal콩콩(inputs).toFixed(1)}`,
        `${cal버럭(inputs).toFixed(1)}`,
      ].join(';')
      const code = score
        .split('')
        .map(el => el.charCodeAt(0))
        .map(el => el.toString(16))
        .join('')
      return code
    },
    age: (state): number => {
      const year = Number(state.birthYear)
      const month = Number(state.birthMonth)
      const date = Number(state.birthDate)
      const afterBirthday =
        new Date().getDate() >= date && new Date().getMonth() + 1 >= month

      return afterBirthday
        ? new Date().getFullYear() - year
        : new Date().getFullYear() - year - 1
    },
    PSS_Score: (state): number => PSS.getScore(state.PSSInputs),
    HMPS_Score: (state): number => HMPS.getScore(state.HMPSInputs),
    BAI_Score: (state): number => BAI.getScore(state.BAIInputs),
    K_SSES_Score: (state): number => K_SSES.getScore(state.K_SSESInputs),
    UCLA_Score: (state): number => UCLA.getScore(state.UCLAInputs),
    possiblePSS (): boolean {
      return !(this.PSS_Score < 17)
    },
    possibleToSubmit (): boolean {
      return !(
        (this.HMPS_Score < 198) // &&
        // this.BAI_Score < 16 &&
        // this.K_SSES_Score > 57
        // this.UCLA_Score < 47
      )
    },
    // valid (state): boolean {
    //   return (
    //     this.ageValid &&
    //     state.agree &&
    //     !!state.email &&
    //     !!state.phone &&
    //     !!state.name
    //   )
    // },
    stageValidity (): boolean {
      if (this.currentStage === 1) {
        return this.이전사용유무 === false
      } else if (this.currentStage === 2) {
        if (Number.isNaN(this.age)) return false
        return this.age >= 18 && this.age < 60
      } else if (this.currentStage === 3) {
        return (
          this.email !== '' &&
          this.phone !== '' &&
          this.agree === true &&
          this.name !== ''
        )
      } else if (this.currentStage === 4) {
        return this.결혼유무 !== '' && this.성별 !== '' && this.직업군 !== ''
      } else if (this.currentStage === 5) {
        return (
          this.uuid !== '' &&
          this.PSS_Score != null &&
          스키마검사.getScore(this.스키마Inputs) != null
        )
      } else if (this.currentStage === 6) {
        return (
          this.uuid !== '' &&
          this.PSS_Score != null &&
          스키마검사.getScore(this.스키마Inputs) != null &&
          HMPS.getScore(this.HMPSInputs) != null &&
          BAI.getScore(this.BAIInputs) != null &&
          K_SSES.getScore(this.K_SSESInputs) != null &&
          UCLA.getScore(this.UCLAInputs) != null
        )
      } else if (this.currentStage === 7) {
        return true
      } else {
        return false
      }
    },
  },
  actions: {
    initStage (stage: number) {
      this.currentStage = stage
    },
    async makeUUID () {
      const res = await makeUUID()
      if (!res.success) {
        alert('서버 오류. 관리자에게 문의하세요')
        throw new Error('ERROR')
      }

      this.uuid = res.data
    },
    async submit1 () {
      if (this.currentStage !== 5) return
      if (!this.stageValidity) return
      if (this.uuid === '') return

      const res = await submit1({
        uuid: this.uuid,
        이전사용: this.이전사용유무 ? 'Y' : 'N',
        생년월일: `${this.birthYear}-${this.birthMonth}-${this.birthDate}`,
        동의여부: this.agree ? 'Y' : 'N',
        이메일: this.email,
        전화번호: this.phone,
        이름: this.name,
        결혼유무: this.결혼유무,
        직업군: this.직업군,
        성별: this.성별,
        PSS_점수: PSS.getScore(this.PSSInputs).toFixed(0),

        PSS_입력: this.PSSInputs.map(String),
        스키마_입력: this.스키마Inputs.map(String),
      })

      return res
    },
    async submit2 () {
      if (this.currentStage !== 6) return
      if (!this.stageValidity) return
      if (this.uuid === '') return

      const res = await submit2({
        uuid: this.uuid,
        이전사용: this.이전사용유무 ? 'Y' : 'N',
        생년월일: `${this.birthYear}-${this.birthMonth}-${this.birthDate}`,
        동의여부: this.agree ? 'Y' : 'N',
        이메일: this.email,
        전화번호: this.phone,
        이름: this.name,
        결혼유무: this.결혼유무,
        직업군: this.직업군,
        성별: this.성별,
        PSS_점수: PSS.getScore(this.PSSInputs).toFixed(0),
        HMPS_점수: HMPS.getScore(this.HMPSInputs).toFixed(0),
        BAI_점수: BAI.getScore(this.BAIInputs).toFixed(0),
        K_SSES_점수: K_SSES.getScore(this.K_SSESInputs).toFixed(0),
        UCLA_점수: UCLA.getScore(this.UCLAInputs).toFixed(0),

        PSS_입력: this.PSSInputs.map(String),
        스키마_입력: this.스키마Inputs.map(String),
        HMPS_입력: this.HMPSInputs.map(String),
        BAI_입력: this.BAIInputs.map(String),
        K_SSES_입력: this.K_SSESInputs.map(String),
        UCLA_입력: this.UCLAInputs.map(String),
      })

      return res
    },
    async submit3 () {
      if (this.currentStage !== 7) return
      if (!this.stageValidity) return
      if (this.uuid === '') return

      const res = await submit2({
        uuid: this.uuid,
        이전사용: this.이전사용유무 ? 'Y' : 'N',
        생년월일: `${this.birthYear}-${this.birthMonth}-${this.birthDate}`,
        동의여부: this.agree ? 'Y' : 'N',
        이메일: this.email,
        전화번호: this.phone,
        이름: this.name,
        결혼유무: this.결혼유무,
        직업군: this.직업군,
        성별: this.성별,
        PSS_점수: PSS.getScore(this.PSSInputs).toFixed(0),
        HMPS_점수: HMPS.getScore(this.HMPSInputs).toFixed(0),
        BAI_점수: BAI.getScore(this.BAIInputs).toFixed(0),
        K_SSES_점수: K_SSES.getScore(this.K_SSESInputs).toFixed(0),
        UCLA_점수: UCLA.getScore(this.UCLAInputs).toFixed(0),

        PSS_입력: this.PSSInputs.map(String),
        스키마_입력: this.스키마Inputs.map(String),
        HMPS_입력: this.HMPSInputs.map(String),
        BAI_입력: this.BAIInputs.map(String),
        K_SSES_입력: this.K_SSESInputs.map(String),
        UCLA_입력: this.UCLAInputs.map(String),
      })

      if (res.success) {
        if (window.localStorage) {
          window.localStorage.removeItem(STORAGE_KEY)
        }
      }

      return res
    },
  },
})

export const initState = () => {
  const store = useStore()
  store.$subscribe(() => {
    if (window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store.$state))
    }
  })
}
